// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Reminders and emails
// ==============================================
// The schedule preview on the skipper's request page. The reminder rule is a
// straight port of vortex-api lib/paperRequestSchedule.ts nextReminderDate —
// keep the two identical (schedule.test.ts pins the design's example):
//   • the send day + k × remindEveryDays (k ≥ 1),
//   • every day of the run-in: the last dailyFinalDays before dueOn,
//   • dueOn itself — never after it.
// Status emails (Phase 2.2): on the chosen cadence, and always on the due date.
// ==============================================

import type { StatusCadence } from './types';
import { addDays, daysBetween, isDate } from './dates';

const dayNumber = (date: string) => daysBetween('1970-01-01', date);
const fromDayNumber = (n: number) => addDays('1970-01-01', n);

export interface ReminderRule { sentLocalDate: string; dueOn: string | null; remindEveryDays: number; dailyFinalDays: number }

/** The first reminder day strictly after `afterLocalDate`, or null when none is left. */
export function nextReminderDate(r: ReminderRule & { afterLocalDate: string }): string | null {
  const every = Math.max(1, Math.floor(r.remindEveryDays));
  const after = dayNumber(r.afterLocalDate);
  const sent = dayNumber(r.sentLocalDate);
  const k = Math.max(1, Math.floor((after - sent) / every) + 1);
  let next = sent + k * every;
  if (r.dueOn) {
    const due = dayNumber(r.dueOn);
    if (after >= due) return null;
    const runIn = due - Math.max(0, Math.floor(r.dailyFinalDays));
    const dailyNext = Math.max(runIn, after + 1);
    next = Math.min(next, dailyNext, due);
  }
  return fromDayNumber(next);
}

export interface ScheduleInput { sentOn: string; dueOn: string | null; remindEveryDays: number; dailyFinalDays: number }

/** Every crew reminder day from the send day to the due date. */
export function reminderDates(s: ScheduleInput): string[] {
  if (!isDate(s.dueOn) || !isDate(s.sentOn) || s.dueOn < s.sentOn) return [];
  const out: string[] = [];
  let after = s.sentOn;
  for (let guard = 0; guard < 400; guard++) {
    const next = nextReminderDate({ sentLocalDate: s.sentOn, dueOn: s.dueOn, remindEveryDays: s.remindEveryDays, dailyFinalDays: s.dailyFinalDays, afterLocalDate: after });
    if (!next) break;
    out.push(next);
    after = next;
  }
  return out;
}

export interface StatusInput { cadence: StatusCadence; weekday: number | null; everyDays: number | null; sentOn: string; dueOn: string | null }

/** Status-email days that can be listed (weekly, every n days), plus the due date. */
export function statusDates(s: StatusInput): string[] {
  if (!isDate(s.dueOn) || !isDate(s.sentOn) || s.dueOn < s.sentOn) return [];
  const out = new Set<string>();
  const span = daysBetween(s.sentOn, s.dueOn);
  for (let i = 1; i <= span; i++) {
    const d = addDays(s.sentOn, i);
    if (s.cadence === 'weekly' && s.weekday !== null && new Date(`${d}T00:00:00Z`).getUTCDay() === s.weekday) out.add(d);
    if (s.cadence === 'every_n_days' && s.everyDays && i % s.everyDays === 0) out.add(d);
  }
  out.add(s.dueOn);
  return [...out].sort();
}

export interface ScheduleRow { from: string; to?: string; label: string; due?: boolean }

export function schedulePreview(s: ScheduleInput & Omit<StatusInput, 'sentOn' | 'dueOn'>): { rows: ScheduleRow[]; note: string | null } {
  const note = s.cadence === 'daily' ? 'Your status email comes every morning at 08:00.'
    : s.cadence === 'each_upload' ? 'You get a status email each time someone shares a paper.' : null;
  if (!isDate(s.dueOn) || !isDate(s.sentOn) || s.dueOn < s.sentOn) return { rows: [], note };
  const reminders = new Set(reminderDates(s));
  const status = new Set(statusDates({ ...s, sentOn: s.sentOn, dueOn: s.dueOn }));
  const days = [...new Set([...reminders, ...status])].filter((d) => d !== s.dueOn).sort();

  const rows: ScheduleRow[] = [{ from: s.sentOn, label: 'Request sent' }];
  for (let i = 0; i < days.length; i++) {
    const d = days[i];
    const r = reminders.has(d); const st = status.has(d);
    if (r && !st) {
      // fold a run of consecutive reminder-only days into one line
      let j = i;
      while (j + 1 < days.length && daysBetween(days[j], days[j + 1]) === 1 && reminders.has(days[j + 1]) && !status.has(days[j + 1])) j++;
      if (j > i) { rows.push({ from: d, to: days[j], label: 'Daily reminders' }); i = j; continue; }
    }
    rows.push({ from: d, label: r && st ? 'Reminder · your status' : r ? 'Reminder' : 'Your status' });
  }
  rows.push({ from: s.dueOn, label: 'Due · your status', due: true });
  return { rows, note };
}
